<template>
    <div :class="['kws-quiz', themeClass]" :id="id">
        <component
            v-if="initialized"
            :is="type"
            :id="id"
            :introduction="introduction"
            :questions="questions"
            :result="result"
            :outro="outro"
            :class="additionalClasses"
        ></component>
    </div>
</template>

<script>
import Assistant from './components/quiz-type-assistant.vue';
import Poll from './components/quiz-type-poll.vue';
import Quiz from './components/quiz-type-quiz.vue';
import Raffle from './components/quiz-type-raffle.vue';

export default {
  name: 'App',
  data: () => ({
    initialized: false,
  }),
  props: {
    id: {
      type: String,
      required: true,
    },
    type: {
      type: String,
      required: true,
    },
    theme: String,
    additionalClasses: String,
    translations: Object,
    introduction: Object,
    questions: Array,
    result: Object,
    outro: Object,
  },
  computed: {
    themeClass() {
      return this.theme ? `theme-${this.theme}` : null;
    },
  },
  created() {
    if (this.translations) {
      this.addDictionary(this.translations);
    }
    this.initialized = true;
  },
  components: {
    Assistant,
    Poll,
    Quiz,
    Raffle,
  },
};
</script>

<style lang="scss">
@keyframes loadingspinner {
    to {transform: rotate(360deg);}
}

.kws-quiz {

    select,
    select.form-element,
    input,
    textarea {
        background-color: $color-kws-light-gray-25;
    }

    &.theme {
        &-corn,
        &-maize {
            background-color: $color-kws-yellow-100;
        }

        &-ackerfit {
            background-color: $color-kws-gray-100;
            color: $color-white;
        }

        &-sugarbeet {
            background-color: $color-kws-green-100;
            color: $color-white;
        }

        &-wheat,
        &-barley,
        &-rye,
        &-grain {
            background-color: $color-kws-light-beige-100;
        }

        &-sorghum {
            background-color: $color-kws-dark-red-100;
            color: $color-white;
        }

        &-grains-pea {
            background-color: $color-kws-red-100;
            color: $color-white;
        }

        &-rape-seed {
            background-color: $color-kws-light-green-100;
            color: $color-white;
        }

        &-sunflower {
            background-color: $color-kws-light-yellow-100;
        }

        &-soy {
            background-color: $color-kws-light-brown-100;
        }

        &-grey {
            background-color: $color-kws-black-5;

            .kws-quiz-introduction,
            .kws-quiz-question,
            .kws-quiz-registration,
            .kws-quiz-outro {
                background-color: $color-white;
            }
        }

        &-white {
            background-color: $color-white;

            .kws-quiz-introduction,
            .kws-quiz-question,
            .kws-quiz-registration,
            .kws-quiz-outro {
                background-color: $color-kws-black-5;
            }

            select,
            input,
            textarea {
                background-color: $color-white;
            }
        }

    }

    section {
        display: block;
    }

    // mobile
    .kws-quiz-img-desktop {
        display: none;
    }

    .kws-int-container {
        padding-top: rem(40px);
        padding-bottom: rem(40px);

        > section {
            &:after {
                display: none;
                opacity: 0;
            }
        }

        &.disabled {
            > section {
                position: relative;
                opacity: .5;
                cursor: wait;
                filter: blur(rem(1px));
            }

            $spinnerDimension: 40;
            &::before {
                content: '';
                box-sizing: border-box;
                position: absolute;
                top: 50%;
                left: 50%;
                width: #{$spinnerDimension}px;
                height: #{$spinnerDimension}px;
                margin-top: -#{$spinnerDimension/2}px;
                margin-left: -#{$spinnerDimension/2}px;
                border-radius: 50%;
                border-top: rem(2px) solid $color-kws-orange;
                border-right: rem(2px) solid transparent;
                animation: loadingspinner 1.4s linear infinite;
                z-index: 2;
            }
        }
    }

    .kws-quiz-img-mobile {
        width: 100%;
    }

    .kws-quiz-text-wrapper {
        display: flex;
        flex-direction: column;
    }

    h2 {
        font-size: rem(28px);
        padding: rem(15px);
        margin-bottom: 0;

        small {
            font-size: rem(20px);
        }

        &::after {
            content: '';
            margin-top: rem(15px);
            display: block;
            width: rem(40px);
            height: rem(2px);
            background-color: $color-kws-orange;
        }
    }

    img {
        flex-shrink: 0;
    }

    p {
        line-height: rem(24px);
        font-size: rem(20px);
        font-weight: 700;
        margin: rem(15px) 0;
    }

    .kws-quiz-text-content {
        padding: 0 rem(15px) rem(15px) rem(15px);
        display: flex;
        flex-direction: column;
    }

    // tablet
    @media (min-width: rem(664px)) {
        .kws-quiz-text-wrapper {
            display: block;
        }

        h2 {
            padding: rem(20px);

            &::after {
                width: rem(60px);
            }
        }

        p {
            margin: rem(20px) 0;
        }

        .kws-quiz-text-content {
            display: block;
            padding: 0 rem(20px) rem(20px) rem(20px);
        }
    }

    // desktop
    @media (min-width: rem(1024px)) {
        section {
            display: flex;
            align-items: stretch;
        }

        .kws-int-container {
            padding-top: rem(80px);
            padding-bottom: rem(80px);
        }

        .kws-quiz-img-desktop {
            display: block;
            min-height: rem(420px);
            width: rem(420px);
            flex-basis: rem(420px);
            flex-shrink: 0;
            flex-grow: 0;
            position: relative;
            align-self: stretch;
            overflow: hidden;

            img {
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                height: 100%;
                width: auto;
                max-width: none;
            }
        }

        .kws-quiz-img-mobile {
            display: none;
        }

        .kws-quiz-text-wrapper {
            width: rem(372px);
            padding: rem(20px) rem(30px);
            flex-grow: 1;
        }
        h2 {
            padding: 0;
            font-size: rem(40px);
            margin-bottom: rem(15px);

            ::after {
                width: rem(80px);
            }

        }
        p {
            margin: 0 0 rem(30px) 0;
            line-height: 2.0rem;
            font-size: 1.5rem;
        }

        .kws-quiz-text-content {
            padding: 0;
        }
    }

  .form-component {
    .floating-input-label__input ~ .location-icon,
    .floating-input-label__input ~ .dropdown-arrow-icon {
      right: auto;
      inset-inline-end: .625rem;
    }
    .floating-input-label__label {
      left: auto;
      inset-inline-start: 0.625rem;
    }
  }

  html[dir=rtl] & .form-component {
    .floating-input-label__label {
      transform-origin: right top;
    }
    select.form-element {
      background-position: 10px 50%;
    }
  }

}
</style>
